import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { NotFoundError, RedirectError } from '#src/shared/error';

import { PAGE_SIZE } from '#src/app/pages/petitionGuides/category/details/shared/consts';

import type { PetitionGuidesCategoryPageDataQuery, PetitionGuidesCategoryPageDataQueryVariables } from './api.graphql';
import { PetitionGuidesCategoryPageData as PetitionGuidesCategoryPageDataQueryDocument } from './api.graphql';

type CtaSection = NonNullable<PetitionGuidesCategoryPageDataQuery['landingPage']>['ctaSection'];

export type PetitionGuidesCategoryPageData = NonNullable<PetitionGuidesCategoryPageDataQuery['category']> & {
	ctaSection: CtaSection | null;
	categories: PetitionGuidesCategoryPageDataQuery['categories'];
	page: number;
	totalPages: number;
};

export async function getPetitionGuidesCategoryPageData(
	{ gql: { fetch } }: UtilityContext,
	slug: string,
	page: number,
): Promise<PetitionGuidesCategoryPageData> {
	// `landingPage` (the CTA section) is fetched with `failSilently: true` — a genuine backend
	// failure on it resolves to `null` rather than throwing, so it can't take down this page over an
	// optional CTA. `categories` (the nav chip list) already fails silently on the resolver side
	// regardless. `category` is the only field here with no such fallback, so it's the only one that
	// can still throw and take the whole query down with it, which is the intended critical/tolerated
	// split — see docs/front-end/graphql.md § Error handling — rejectOnError.
	const { data } = await fetch<PetitionGuidesCategoryPageDataQuery, PetitionGuidesCategoryPageDataQueryVariables>({
		query: PetitionGuidesCategoryPageDataQueryDocument,
		variables: { slug, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
		rejectOnError: true,
	});

	// `category` is null when there's genuinely no matching category slug — that's our real 404
	// signal. An empty `category.articles` list is a legitimately empty category, not a 404.
	if (!data.category) {
		throw new NotFoundError();
	}

	const totalPages = Math.max(1, Math.ceil(data.category.totalArticleCount / PAGE_SIZE));

	// Page 1 of a 0-article category has totalPages === 1, so it renders the empty state
	// instead of redirecting here.
	//
	// A page beyond the last one redirects to the bare category URL rather than 404ing: unlike an
	// unknown slug, this is a real category that simply doesn't have that many pages right now — it
	// might later, if content ops add articles — so treating it as permanently gone is wrong. The
	// redirect target uses the fetched `data.category.slug` rather than the input `slug`, matching
	// `pageCanonical`, and drops `page` entirely rather than forwarding it, since the whole point is
	// to land on a page number that's valid.
	if (page > totalPages) {
		throw new RedirectError({ url: `/petition-guides/category/${data.category.slug}` });
	}

	return {
		...data.category,
		ctaSection: data.landingPage?.ctaSection ?? null,
		categories: data.categories,
		page,
		totalPages,
	};
}
